import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Paper, Grid, Card, CardContent, Chip, LinearProgress, Alert } from '@mui/material';
import { AccountBalance, CheckCircle, Warning } from '@mui/icons-material';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { fetchApi } from '@/api/fetchApi';

/**
 * Trial balance widget — calls `GET /api/v1/ledger/trial-balance` and renders
 * debit/credit totals with a balanced badge (LedgerFunctionsAndProcesses §7).
 * Gated on the `ledger` domain entitlement; hidden without it.
 *
 * @see Documentation/Specification/01_Requirements/application-agnostic/ledger/LedgerFunctionsAndProcesses.md §7
 * @see sprints/2026-10/Milestone_01_CompleteLedgerImplementation/sprint-07-reporting-period-close/README.md T7.5
 */
interface TrialBalancePayload {
  asOf?: string;
  totalDebit?: number;
  totalCredit?: number;
  balanced?: boolean;
  accountCount?: number;
  zeroBalanceCount?: number;
}

export const TrialBalanceCard = () => {
  const { hasEntitlement } = useEntitlement(DEFAULT_TENANT_ID);
  const entitled = hasEntitlement('ledger');

  const { data, isLoading, error } = useQuery<TrialBalancePayload>({
    queryKey: ['trial-balance'],
    queryFn: () => fetchApi('/ledger/trial-balance'),
    enabled: entitled,
    staleTime: 60_000,
  });

  if (!entitled) return null;
  if (isLoading) return <LinearProgress sx={{ mb: 2 }} />;
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Trial balance unavailable
      </Alert>
    );
  }

  const balanced = data?.balanced === true;

  return (
    <Card sx={{ mb: 3 }} data-testid="trial-balance-card">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Trial Balance</Typography>
          </Box>
          <Chip
            data-testid="trial-balance-badge"
            label={balanced ? 'Balanced' : 'Unbalanced'}
            color={balanced ? 'success' : 'error'}
            icon={balanced ? <CheckCircle /> : <Warning />}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total Debit</Typography>
              <Typography variant="h6" color="error.main" data-testid="trial-balance-debit">
                {(data?.totalDebit ?? 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total Credit</Typography>
              <Typography variant="h6" color="success.main" data-testid="trial-balance-credit">
                {(data?.totalCredit ?? 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Accounts</Typography>
              <Typography variant="h6">{data?.accountCount ?? 0}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Zero Balances</Typography>
              <Typography variant="h6">{data?.zeroBalanceCount ?? 0}</Typography>
            </Paper>
          </Grid>
        </Grid>
        {data?.asOf && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            As at {data.asOf}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
